// EligibilityRequirements.js - Portugal
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Briefcase, DollarSign, Languages, GraduationCap } from 'lucide-react';

const EligibilityRequirements = () => {
  const requirements = [
    {
      icon: DollarSign,
      title: 'Financial Requirements',
      description: 'The Golden Visa starts at a €500,000 investment. The D7 asks instead for €820 a month in passive income, matching the Portuguese minimum wage. Either way, you document the funds and where they came from.',
      applicable: 'Golden Visa, D7 Visa'
    },
    {
      icon: Briefcase,
      title: 'Investment Documentation',
      description: 'Golden Visa applicants show the legal source of their funds, along with investment contracts, property deeds or business registration. Whichever option you pick, the investment has to be held for at least 5 years.',
      applicable: 'Golden Visa'
    },
    {
      icon: Languages,
      title: 'Portuguese Language',
      description: 'Nothing is asked of you at the residency stage. Portuguese at A2 level only becomes necessary when you apply for citizenship 5 years in, and courses are easy to find once you are living there.',
      applicable: 'Citizenship (after 5 years)'
    },
    {
      icon: GraduationCap,
      title: 'Accommodation Proof',
      description: 'Every visa type wants proof of somewhere to stay, whether a tenancy agreement, a property you own or a hotel booking. This can be settled after the visa has been approved.',
      applicable: 'All Programs'
    },
    {
      icon: CheckCircle2,
      title: 'Clean Criminal Record',
      description: 'You supply a police clearance certificate from where you currently live, plus one from any country you spent 12 months or more in over the past 5 years. Serious convictions rule an application out.',
      applicable: 'All Programs'
    },
    {
      icon: FileText,
      title: 'Valid Travel Documents',
      description: 'A passport valid for at least 3 months beyond the stay you intend, and travel insurance covering €30,000 or more for medical costs and repatriation.',
      applicable: 'All Programs'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            What You Will Need to Show
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Requirements shift from one programme to the next, but Portuguese residency applications tend to rest on the same set of documents.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requirements.map((requirement, index) => {
            const Icon = requirement.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-surface p-6 rounded-lg border border-gray-200 hover:border-secondary transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-primary mb-1">
                      {requirement.title}
                    </h3>
                    <span className="text-xs text-secondary font-light">
                      {requirement.applicable}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {requirement.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-primary to-secondary rounded-lg p-8 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">
            Golden Visa Entry Points
          </h3>
          <p className="text-white/90 text-sm font-light leading-relaxed mb-4">
            More than one kind of investment will qualify you. These are the headline figures for the three most used.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Capital Transfer</p>
              <p className="text-white/80 font-light">€500,000</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Investment Funds</p>
              <p className="text-white/80 font-light">€500,000</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Job Creation</p>
              <p className="text-white/80 font-light">10 jobs</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Please note:</strong> Portuguese immigration policy is revised from time to time, and the investment thresholds move with it. We check your circumstances against the rules as they stand and advise on which route makes sense.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EligibilityRequirements;
